<script lang="ts" setup>
import { computed } from 'vue'
import { startCase } from 'lodash'
import { getTerminalLabel } from '~/constants'
import type { BasicContainer } from '~/models/containers'
import { ContainerWatchState } from '~/services/apiClient'

interface Props {
  selectedContainer: BasicContainer | null
  emptyContainerNumber: string
  loadingEmptyData: boolean
}

const props = defineProps<Props>()

const showOutgatedInfo = computed(
  () => props.selectedContainer?.import_out_gated_time
)

const showStatusWarning = computed(
  () =>
    props.selectedContainer &&
    props.selectedContainer.cycle_state !== ContainerWatchState.ImportPickedUp
)

const showNotFoundWarning = computed(
  () =>
    !props.selectedContainer &&
    props.emptyContainerNumber &&
    !props.loadingEmptyData
)

const outgatedTimeText = computed(() =>
  props.selectedContainer?.import_out_gated_time?.toRelative()
)

const terminalText = computed(() => {
  const terminal = props.selectedContainer?.last_related_terminal
  return terminal ? getTerminalLabel(terminal) : null
})

const statusText = computed(() =>
  props.selectedContainer ? startCase(props.selectedContainer.cycle_state) : ''
)
</script>

<template>
  <div class="container-info-section">
    <template v-if="selectedContainer">
      <div v-if="showOutgatedInfo" class="info-line">
        Container was out-gated {{ outgatedTimeText }}
        <template v-if="terminalText"> from {{ terminalText }} </template>
      </div>
      <div v-if="showStatusWarning" class="info-line text-red-600">
        Container not an out-gated import. Status:
        <i>{{ statusText }}</i>
      </div>
    </template>
    <div v-if="showNotFoundWarning" class="info-line text-orange-400">
      {{ emptyContainerNumber }} not found in Dray Dog.
      <span class="text-black">
        This is fine, just double check you have a valid empty
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.container-info-section {
  min-height: 24px;
  margin-top: 4px;

  .info-line {
    line-height: 1.4;
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
